import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AuthModal } from "@/components/AuthModal";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

const PERKS = [
  "Every Premium Guide on every blog post",
  "All downloadable resources & PDFs",
  "Full 28-Day Roadmap on every article",
  "Entire Digital Resources marketplace",
  "Cancel anytime — keep access until period end",
];

export default function Pricing() {
  const [user, setUser] = useState<User | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const { openCheckout, checkoutDialog } = useStripeCheckout();
  const { isActive, subscription } = useSubscription(user);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((_e, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u && pendingPriceId) {
        const priceId = pendingPriceId;
        setPendingPriceId(null);
        openCheckout({ priceId, title: "Subscribe to All-Access" });
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = (priceId: string) => {
    if (!user) {
      setPendingPriceId(priceId);
      setAuthOpen(true);
      return;
    }
    openCheckout({ priceId, title: "Subscribe to All-Access" });
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/my-learning`,
        },
      });
      if (error || !data?.url) throw new Error(data?.error || error?.message || "Failed");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open portal");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Pricing — All-Access Membership | RunoBlog</title>
        <meta
          name="description"
          content="Unlock every premium guide, resource, and 28-day roadmap on RunoBlog with one membership."
        />
      </Helmet>
      <PaymentTestModeBanner />
      <Navigation />

      <section className="container mx-auto px-4 py-16 max-w-5xl">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">
            <Sparkles className="h-3 w-3 mr-1" />
            All-Access Membership
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            One subscription. Everything unlocked.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Skip per-item purchases. Get unlimited access to every premium guide,
            downloadable resource, and 28-day roadmap on RunoBlog.
          </p>
        </div>

        {isActive && (
          <Card className="p-6 mb-8 border-primary bg-primary/5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold flex items-center gap-2">
                  <Check className="h-5 w-5 text-primary" />
                  You have an active membership
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Plan: <span className="font-mono">{subscription?.price_id}</span>
                  {subscription?.cancel_at_period_end && subscription?.current_period_end && (
                    <> · Cancels on {new Date(subscription.current_period_end).toLocaleDateString()}</>
                  )}
                </p>
              </div>
              <Button onClick={openPortal} disabled={portalLoading} variant="outline">
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Manage subscription"}
              </Button>
            </div>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Monthly</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">$9.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Billed monthly. Cancel anytime.</p>
            </div>
            <Button
              size="lg"
              className="w-full mb-6"
              onClick={() => handleSubscribe("all_access_monthly")}
              disabled={isActive}
            >
              {isActive ? "Already subscribed" : "Start monthly"}
            </Button>
            <ul className="space-y-3">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-8 border-primary border-2 relative">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Save $40</Badge>
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Yearly</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">$79</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                ~$6.58/month · Best value
              </p>
            </div>
            <Button
              size="lg"
              className="w-full mb-6"
              onClick={() => handleSubscribe("all_access_yearly")}
              disabled={isActive}
            >
              {isActive ? "Already subscribed" : "Start yearly"}
            </Button>
            <ul className="space-y-3">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-12">
          Prefer to buy one at a time? You can also unlock individual{" "}
          <Link to="/" className="underline hover:text-primary">premium guides</Link> or{" "}
          <Link to="/premium-resources" className="underline hover:text-primary">digital resources</Link>{" "}
          for $3 each.
        </p>
      </section>

      <Footer />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      {checkoutDialog}
    </div>
  );
}
